import React from "react";
import { useAuth } from "../context/AuthContext";

/**
 * AdSlot — renders a Google AdSense unit for non-premium users.
 * When REACT_APP_ADSENSE_CLIENT is set (e.g. ca-pub-1234...), real ads render.
 * Otherwise shows a subtle newspaper-styled placeholder.
 *
 * Publisher setup:
 *  1. Sign up at https://www.google.com/adsense once the app is deployed on a real domain.
 *  2. Set REACT_APP_ADSENSE_CLIENT in frontend/.env to your publisher ID.
 *  3. Create ad units in AdSense, use their slot IDs via the `slot` prop.
 */
export default function AdSlot({ slot = "auto", label = "Advertisement", className = "" }) {
  const { user } = useAuth();
  const hasFeatures = !!user?.subscription?.has_features;
  const publisher = process.env.REACT_APP_ADSENSE_CLIENT;

  React.useEffect(() => {
    if (!publisher || hasFeatures) return;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (_) {}
  }, [publisher, hasFeatures]);

  if (hasFeatures) return null;

  if (!publisher) {
    return (
      <div className={`border border-dashed border-border rounded-lg py-6 px-4 text-center bg-card/50 ${className}`} data-testid="ad-slot-placeholder">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-newspaper mb-1">— Advertisement —</div>
        <div className="text-sm text-muted-foreground font-newspaper italic">Your ad space · <a href="/pricing" className="underline">go ad-free</a></div>
      </div>
    );
  }

  return (
    <div className={`text-center ${className}`} data-testid="ad-slot">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-newspaper mb-1">{label}</div>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={publisher}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
